/**
 * Process Conditions Section
 *
 * Thin wrapper around ScopeEditorSection for process-condition attributes.
 */

import React, { useMemo } from 'react';
import ScopeEditorSection from './ScopeEditorSection';
import { useRevitParameterOptions } from '../../../../shared/hooks/useRevitParameterOptions';
import {
  useMenuConfiguration,
  useConnectorBindingConfiguration,
} from '../../../../shared/stores/useConfigurationStore';
import {
  DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
  PROCESS_CONDITIONS_FIELD_BY_EDITOR_ROW_ID,
  PROCESS_CONDITIONS_EDITOR_ROW_ORDER,
} from '../../../../shared/config/mappingScopeEditorFieldDefaults';
import { buildScopeEditorAttributes } from '../../lib/buildScopeEditorAttributes';
import {
  buildPcfAttributeOptions,
  buildPreviousPagesOptions,
} from '../../lib/buildScopeEditorOptions';
import type { DropdownOption } from '../../../../shared/ui/forms/SearchableDropdown';
import type {
  ScopeEditorPersistenceState,
  ScopeEditorPersistenceStateUpdater,
} from '../../lib/scopeEditorPersistence';

export { DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES };

interface ProcessConditionsSectionProps {
  enabled: boolean;
  processConditionsValue: { value: string };
  pipingSpecValuePC: { value: string };
  tracingSpecValuePC: { value: string };
  attribute12Value: { value: string };
  attribute13Value: { value: string };
  attribute14Value: { value: string };
  attribute15Value: { value: string };
  attribute16Value: { value: string };
  attribute17Value: { value: string };
  attribute18Value: { value: string };
  attribute19Value: { value: string };
  attribute20Value: { value: string };
  attribute21Value: { value: string };
  attribute22Value: { value: string };
  attribute23Value: { value: string };
  onProcessConditionsValueChange: (value: string) => void;
  onPipingSpecValuePCChange: (value: string) => void;
  onTracingSpecValuePCChange: (value: string) => void;
  onAttribute12ValueChange: (value: string) => void;
  onAttribute13ValueChange: (value: string) => void;
  onAttribute14ValueChange: (value: string) => void;
  onAttribute15ValueChange: (value: string) => void;
  onAttribute16ValueChange: (value: string) => void;
  onAttribute17ValueChange: (value: string) => void;
  onAttribute18ValueChange: (value: string) => void;
  onAttribute19ValueChange: (value: string) => void;
  onAttribute20ValueChange: (value: string) => void;
  onAttribute21ValueChange: (value: string) => void;
  onAttribute22ValueChange: (value: string) => void;
  onAttribute23ValueChange: (value: string) => void;
  editorState?: ScopeEditorPersistenceState;
  onEditorStateChange?: (nextState: ScopeEditorPersistenceStateUpdater) => void;
}

const ProcessConditionsSection: React.FC<ProcessConditionsSectionProps> = (props) => {
  const { options: revitParamOptions } = useRevitParameterOptions({
    preferredParameterType: 'Instance',
  });
  const menuConfig = useMenuConfiguration();
  const connectorConfig = useConnectorBindingConfiguration();

  const previousPagesOptions = buildPreviousPagesOptions([
    { value: menuConfig.pipingSpecValue, label: 'PIPING-SPEC (Menu)', source: 'Menu settings' },
    {
      value: connectorConfig.tracingSpecValue?.value,
      label: 'TRACING-SPEC (Connector)',
      source: 'Connector binding',
    },
    {
      value: connectorConfig.materialValue?.value,
      label: 'MATERIAL (Connector)',
      source: 'Connector binding',
    },
    {
      value: connectorConfig.designStdValue?.value,
      label: 'DESIGN-STD (Connector)',
      source: 'Connector binding',
    },
    {
      value: connectorConfig.pressureClassValue?.value,
      label: 'PRESSURE-CLASS (Connector)',
      source: 'Connector binding',
    },
  ]);

  const revitParameterOptions: DropdownOption[] = [...previousPagesOptions, ...revitParamOptions];

  const pcfAttributeOptions: DropdownOption[] = useMemo(
    () =>
      buildPcfAttributeOptions(
        PROCESS_CONDITIONS_EDITOR_ROW_ORDER,
        DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
        () => 'Process conditions'
      ),
    []
  );

  const attributes = useMemo(
    () =>
      buildScopeEditorAttributes(
        PROCESS_CONDITIONS_EDITOR_ROW_ORDER,
        DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
        PROCESS_CONDITIONS_FIELD_BY_EDITOR_ROW_ID,
        props
      ),
    [props]
  );

  return (
    <ScopeEditorSection
      enabled={props.enabled}
      ariaLabel="Process condition PCF attribute source editor"
      disabledMessage="Process condition mappings belong to the setup scope. Enable Process conditions in setup to map their PCF attributes."
      attributes={attributes}
      pcfAttributeOptions={pcfAttributeOptions}
      revitParameterOptions={revitParameterOptions}
      editorState={props.editorState}
      onEditorStateChange={props.onEditorStateChange}
    />
  );
};

export default ProcessConditionsSection;
